import { BellIcon } from "@/components/ui/bell-icon"
import { ChartSplineIcon } from "@/components/ui/chart-spline"
import { ChessKingIcon } from "@/components/ui/chess-king-icon"
import { CpuIcon } from "@/components/ui/cpu-icon"
import { CreditCardIcon } from "@/components/ui/credit-card-icon"
import { LaptopMinimalCheckIcon } from "@/components/ui/laptop-minimal-check-icon"
import { ShieldCheckIcon } from "@/components/ui/shield-check-icon"
import { SlidersHorizontalIcon } from "@/components/ui/sliders-horizontal-icon"
import { UserIcon } from "@/components/ui/user-icon"

export type AccountSettingsAnimatedIcon =
  | typeof UserIcon
  | typeof ShieldCheckIcon
  | typeof CreditCardIcon
  | typeof SlidersHorizontalIcon
  | typeof BellIcon
  | typeof ChessKingIcon
  | typeof CpuIcon
  | typeof ChartSplineIcon
  | typeof LaptopMinimalCheckIcon

/** Same key as legacy sidebar (`SidebarContent.tsx`, `nav-user.tsx`). */
export const ACCOUNT_SETTINGS_RETURN_PATH_KEY =
  "account_settings_sidebar_return_path"

export const ACCOUNT_SETTINGS_BASE_PATH = "/profile"

export const ACCOUNT_SETTINGS_BREADCRUMB_LABEL = "Account Settings"

export const ACCOUNT_SETTINGS_TABS = [
  { id: "general", label: "General", icon: UserIcon },
  { id: "authentication", label: "Authentication", icon: ShieldCheckIcon },
  { id: "billing", label: "Billing Information", icon: CreditCardIcon },
  { id: "display-preferences", label: "Display Preferences", icon: SlidersHorizontalIcon },
  { id: "notifications", label: "Notifications", icon: BellIcon },
  { id: "milestones", label: "Milestones", icon: ChessKingIcon },
  { id: "broker-api", label: "Broker Management", icon: CpuIcon },
  {
    id: "instrument-market-data",
    label: "Instrument market data",
    icon: ChartSplineIcon,
  },
  { id: "devices", label: "Devices", icon: LaptopMinimalCheckIcon },
] as const satisfies ReadonlyArray<{
  id: string
  label: string
  icon: AccountSettingsAnimatedIcon
}>

export type AccountSettingsTabId =
  (typeof ACCOUNT_SETTINGS_TABS)[number]["id"]

export const ACCOUNT_SETTINGS_DEFAULT_TAB: AccountSettingsTabId = "general"

export const ACCOUNT_SETTINGS_BREADCRUMB_PATH = accountSettingsTabPath(
  ACCOUNT_SETTINGS_DEFAULT_TAB
)

export function accountSettingsTabPath(tabId: AccountSettingsTabId): string {
  return `${ACCOUNT_SETTINGS_BASE_PATH}/${tabId}`
}

export function isKnownAccountSettingsTabId(
  id: string | undefined
): id is AccountSettingsTabId {
  return ACCOUNT_SETTINGS_TABS.some((tab) => tab.id === id)
}

/** Path segment `/profile/:tab` with legacy `?tab=` fallback. */
export function resolveAccountSettingsTabId(
  pathname: string,
  search = ""
): AccountSettingsTabId {
  const legacyTab = new URLSearchParams(search).get("tab")
  if (legacyTab && isKnownAccountSettingsTabId(legacyTab)) {
    return legacyTab
  }

  const segment = pathname
    .replace(/^\/profile\/?/, "")
    .split("/")
    .filter(Boolean)[0]

  if (segment && isKnownAccountSettingsTabId(segment)) {
    return segment
  }

  return ACCOUNT_SETTINGS_DEFAULT_TAB
}

export function isAccountSettingsPathname(pathname: string): boolean {
  return (
    pathname === ACCOUNT_SETTINGS_BASE_PATH ||
    pathname.startsWith(`${ACCOUNT_SETTINGS_BASE_PATH}/`)
  )
}

/** Strip legacy `tab` and build a path-based profile URL. */
export function accountSettingsPathFromLocation(
  pathname: string,
  search: string
): string {
  const tabId = resolveAccountSettingsTabId(pathname, search)
  const params = new URLSearchParams(search)
  params.delete("tab")
  const qs = params.toString()
  return accountSettingsTabPath(tabId) + (qs ? `?${qs}` : "")
}
